import {useCallback, useEffect, useRef, useState} from 'react'
import {ActivityIndicator, Pressable, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useFocusEffect, useNavigation} from '@react-navigation/native'
import {useQuery} from '@tanstack/react-query'
import {Image} from 'expo-image'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {useSetMinimalShellMode} from '#/state/shell'
import * as Layout from '#/components/Layout'
import {SearchInput} from '#/components/forms/SearchInput'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Text} from '#/components/Typography'
import {List} from '#/view/com/util/List'
import {searchMovies, getPosterUrl, type MediaItem} from '#/lib/tmdb'
import {useDebouncedValue} from '#/components/live/utils'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'Library'>

const DEFAULT_SEARCH_TERM = 'Walking'
const DEBOUNCE_DELAY_MS = 300

export function LibraryScreen({}: Props) {
  const navigation = useNavigation()
  const setMinimalShellMode = useSetMinimalShellMode()
  const {_} = useLingui()
  const t = useTheme()
  const [searchText, setSearchText] = useState(DEFAULT_SEARCH_TERM)
  const searchInputRef = useRef<any>(null)
  const requestIdRef = useRef(0)

  useFocusEffect(
    useCallback(() => {
      setMinimalShellMode(false)
    }, [setMinimalShellMode]),
  )

  // Debounce the search text - this is the query that triggers API calls
  const trimmedSearchText = searchText.trim()
  const debouncedQuery = useDebouncedValue(trimmedSearchText, DEBOUNCE_DELAY_MS)

  // Determine the actual query to use for API calls
  // - Use debounced query if it's not empty
  // - Use empty string if input is empty (prevents fetching)
  const query = debouncedQuery.length > 0 ? debouncedQuery : ''

  // Fetch movies when debounced query changes
  const {
    data: movies = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tmdb-search', query],
    queryFn: async ({signal}) => {
      // Increment request ID to track the current request
      const currentRequestId = ++requestIdRef.current

      // If query is empty, return empty array
      if (!query) {
        return []
      }

      try {
        const results = await searchMovies(query, signal)

        // Check if this request is still the latest one
        // If requestId changed, this response is stale and should be ignored
        if (currentRequestId !== requestIdRef.current) {
          // Return empty array to prevent stale data from being set
          // React Query will handle the cancellation via AbortSignal
          return []
        }

        return results
      } catch (e) {
        // If request was aborted or cancelled, re-throw to let React Query handle it
        if (e instanceof Error && e.name === 'AbortError') {
          throw e
        }
        // For other errors, check if this request is still current
        if (currentRequestId !== requestIdRef.current) {
          // Stale request, return empty to prevent stale error state
          return []
        }
        throw e
      }
    },
    enabled: query.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Prevent duplicate requests for the same query
    gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes
  })

  // Auto-fetch on mount with default search term
  useEffect(() => {
    // The query will automatically trigger on mount since searchText starts as DEFAULT_SEARCH_TERM
    // and debouncedQuery will update after DEBOUNCE_DELAY_MS
  }, []) // Empty deps - only run on mount

  const handleClear = useCallback(() => {
    setSearchText('')
    searchInputRef.current?.focus()
  }, [])

  const renderItem = useCallback(
    ({item}: {item: MediaItem}) => {
      const posterUrl = getPosterUrl(item.poster_path)
      const releaseYear = item.release_date
        ? new Date(item.release_date).getFullYear()
        : null
      const mediaType = item.media_type === 'tv' ? 'TV Show' : 'Movie'

      const handlePress = () => {
        ;(navigation as any).navigate('Video', {
          id: item.id,
          title: item.title,
          media_type: item.media_type,
          poster_path: item.poster_path,
          release_date: item.release_date,
          overview: item.overview,
          vote_average: typeof item.vote_average === 'number' ? item.vote_average : 0,
          wt: item.media_type === 'tv' ? 2 : 3, // WT=2 for TV shows, WT=3 for movies
        })
      }

      return (
        <Pressable
          onPress={handlePress}
          style={({pressed}) => [
            a.flex_row,
            a.gap_md,
            a.px_lg,
            a.py_md,
            a.border_t,
            t.atoms.border_contrast_low,
            pressed && {
              backgroundColor: t.palette.contrast_50,
            },
          ]}>
          {/* Poster */}
          <View
            style={[
              {
                width: 100,
                height: 150,
                borderRadius: 8,
                overflow: 'hidden',
                backgroundColor: t.palette.contrast_50,
              },
            ]}>
            {posterUrl ? (
              <Image
                source={{uri: posterUrl}}
                style={{width: '100%', height: '100%'}}
                contentFit="cover"
                transition={200}
              />
            ) : (
              <View
                style={[
                  a.flex_1,
                  a.align_center,
                  a.justify_center,
                  a.px_sm,
                ]}>
                <Text
                  style={[
                    a.text_center,
                    a.text_sm,
                    t.atoms.text_contrast_medium,
                  ]}>
                  No Poster
                </Text>
              </View>
            )}
          </View>

          {/* Media Info */}
          <View style={[a.flex_1, a.gap_xs]}>
            <View style={[a.flex_row, a.align_center, a.gap_xs, a.flex_wrap]}>
              <Text style={[a.text_lg, a.font_bold, a.leading_tight]}>
                {item.title}
              </Text>
              <Text
                style={[
                  a.text_xs,
                  a.font_medium,
                  t.atoms.text_contrast_medium,
                  {
                    backgroundColor: t.palette.contrast_100,
                    paddingHorizontal: 6,
                    paddingVertical: 2,
                    borderRadius: 4,
                  },
                ]}>
                {mediaType}
              </Text>
            </View>
            {releaseYear && (
              <Text
                style={[a.text_sm, t.atoms.text_contrast_medium, a.leading_snug]}>
                {releaseYear}
              </Text>
            )}
            <View style={[a.flex_row, a.align_center, a.gap_xs]}>
              <Text
                style={[a.text_sm, a.font_semi_bold, t.atoms.text_contrast_high]}>
                ⭐ {typeof item.vote_average === 'number' ? item.vote_average.toFixed(1) : 'N/A'}
              </Text>
            </View>
            <Text
              style={[
                a.text_sm,
                t.atoms.text_contrast_medium,
                a.leading_snug,
                {
                  // Truncate to ~3 lines
                  maxHeight: 60,
                },
              ]}
              numberOfLines={3}>
              {item.overview || 'No overview available.'}
            </Text>
          </View>
        </Pressable>
      )
    },
    [t, navigation],
  )

  const renderEmpty = useCallback(() => {
    // Show loading state while typing + fetching
    if (isLoading) {
      return (
        <View style={[a.flex_1, a.align_center, a.justify_center, a.py_3xl]}>
          <ActivityIndicator size="large" />
        </View>
      )
    }

    // Show error state
    if (error) {
      return (
        <View
          style={[
            a.flex_1,
            a.align_center,
            a.justify_center,
            a.px_lg,
            a.py_3xl,
            a.gap_md,
          ]}>
          <Text
            style={[
              a.text_center,
              a.text_lg,
              a.font_semi_bold,
              t.atoms.text_contrast_high,
            ]}>
            <Trans>An error occurred</Trans>
          </Text>
          <Text
            style={[
              a.text_center,
              a.text_sm,
              t.atoms.text_contrast_medium,
              a.leading_snug,
            ]}>
            {error instanceof Error ? error.message : String(error)}
          </Text>
          <Button
            label={_(msg`Retry search`)}
            size="small"
            variant="solid"
            color="primary"
            onPress={() => refetch()}>
            <ButtonText>
              <Trans>Retry</Trans>
            </ButtonText>
          </Button>
        </View>
      )
    }

    // Show empty state hint if input is empty
    if (trimmedSearchText.length === 0) {
      return (
        <View
          style={[
            a.flex_1,
            a.align_center,
            a.justify_center,
            a.px_lg,
            a.py_3xl,
          ]}>
          <Text
            style={[
              a.text_center,
              a.text_lg,
              a.font_semi_bold,
              t.atoms.text_contrast_high,
            ]}>
            <Trans>Start typing to search for movies</Trans>
          </Text>
        </View>
      )
    }

    // Show no results message
    if (movies.length === 0 && query) {
      return (
        <View
          style={[
            a.flex_1,
            a.align_center,
            a.justify_center,
            a.px_lg,
            a.py_3xl,
          ]}>
          <Text
            style={[
              a.text_center,
              a.text_lg,
              a.font_semi_bold,
              t.atoms.text_contrast_high,
            ]}>
            No results for "{query}"
          </Text>
        </View>
      )
    }

    return null
  }, [isLoading, error, movies.length, query, trimmedSearchText, refetch, _, t])

  // Show loading indicator while typing (debounce delay) or fetching
  const isTyping = trimmedSearchText !== debouncedQuery
  const showLoading = isLoading || isTyping

  return (
    <Layout.Screen testID="libraryScreen">
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Library</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>

      {/* Search Bar */}
      <View
        style={[
          a.px_lg,
          a.pt_md,
          a.pb_md,
          a.align_center,
          {
            maxWidth: 600,
            alignSelf: 'center',
            width: '100%',
          },
        ]}>
        <SearchInput
          ref={searchInputRef}
          value={searchText}
          onChangeText={setSearchText}
          placeholder="Walking"
          onClearText={handleClear}
        />
      </View>

      {/* Movie List */}
      {showLoading && movies.length === 0 ? (
        <View style={[a.flex_1, a.align_center, a.justify_center, a.py_3xl]}>
          <ActivityIndicator size="large" />
        </View>
      ) : (
        <List
          data={movies}
          renderItem={renderItem}
          keyExtractor={item => `${item.media_type}-${item.id}`}
          ListEmptyComponent={renderEmpty}
          contentContainerStyle={movies.length === 0 ? a.flex_1 : undefined}
        />
      )}
    </Layout.Screen>
  )
}
